// Home-grown error reporting (replaces Sentry). Sends scrubbed, rate-limited reports to
// POST /v3/client-errors/; the API groups them and alerts Slack on new/regressed errors.

const APP = "total";
const ENDPOINT = `${process.env.REACT_APP_SNUG_API_URL}/v3/client-errors/`;
const ENABLED = !!process.env.REACT_APP_SNUG_API_URL && process.env.REACT_APP_SNUG_ENV !== "LOCAL";
const MAX_PER_MINUTE = 10;
const DEDUPE_MS = 60_000;
const MAX_MESSAGE = 1000;
const MAX_STACK = 8000;

let udId: string | null = null;
let windowStart = 0;
let sentInWindow = 0;
const lastSent = new Map<string, number>();

const PATTERNS: [RegExp, string][] = [
  [/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[token]"],
  [/\b(bearer|token|api[_-]?key|password|secret)(\s*[:=]\s*|\s+)[^\s,;"']+/gi, "$1$2[redacted]"],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]"],
  [/\b\d{3}-\d{2}-\d{4}\b/g, "[ssn]"],
  [/(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}\b/g, "[phone]"],
  [/\b\d{9,}\b/g, "[number]"],
];

// Mirrors the server scrubber (error_tracking.py), which is authoritative; no lookbehind for older Safari
const scrub = (text: string | undefined | null, limit: number): string | undefined => {
  if (!text) return undefined;
  let out = String(text);
  for (const [re, sub] of PATTERNS) out = out.replace(re, sub);
  return out.slice(0, limit);
};

export const setErrorReportingUser = (id: string | null | undefined) => {
  udId = id || null;
};

export const reportError = (error: unknown, extra: { method?: string; status_code?: number } = {}) => {
  try {
    if (!ENABLED) return;
    const err = error instanceof Error ? error : new Error(typeof error === "string" ? error : JSON.stringify(error));
    const message = scrub(`${err.name}: ${err.message}`, MAX_MESSAGE);
    if (!message) return;

    const now = Date.now();
    if (now - (lastSent.get(message) || 0) < DEDUPE_MS) return;
    if (now - windowStart > 60_000) {
      windowStart = now;
      sentInWindow = 0;
    }
    if (sentInWindow >= MAX_PER_MINUTE) return;
    sentInWindow += 1;
    lastSent.set(message, now);

    fetch(ENDPOINT, {
      method: "POST",
      keepalive: true,
      // text/plain keeps this a "simple" request: no CORS preflight
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({
        app: APP,
        message,
        stack: scrub(err.stack, MAX_STACK),
        url: window.location.origin + window.location.pathname,
        release: process.env.REACT_APP_VERSION,
        ud_id: udId,
        ...extra,
      }),
    }).catch(() => {});
  } catch {
    // never let reporting break the app
  }
};

let installed = false;
export const installErrorReporting = () => {
  if (installed || !ENABLED) return;
  installed = true;
  window.addEventListener("error", (event) => {
    // resource load errors have no error object and are mostly noise
    if (event.error || event.message) reportError(event.error || event.message);
  });
  window.addEventListener("unhandledrejection", (event) => reportError(event.reason));
};
